import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MeetingCameraIcon, PlayIcon } from '@rsos/assets/icons';
import { AttentionTriangleIcon } from '@rsos/base-assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import {
  setCardsToRender,
  MEDIA_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  usePostMediaRequestMutation,
  MEDIA_UPLOAD,
  VIDEO_STREAMING,
} from '@rsos/reduxDataStore/src/features/media/mediaAPI';
import { setMedia } from '@rsos/reduxDataStore/src/features/media/mediaSlice';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import {
  trackMediaLiveVideoClick,
  trackMediaRequestMediaClick,
  trackDisplayMediaClick,
  trackMediaRequest,
} from '../mediaTrackings';
import {
  useHasAccessToDisplayMedia,
  useHasAccessToLiveVideo,
  useHasAccessToRequestMedia,
  useHandleMedia,
  useSelectedMediaData,
  useSelectedVideoData,
} from './selectors/selectors';

const ButtonContainer = ({
  isBtnDisabled = false,
  onBtnClick = () => {},
  children,
}) => {
  return (
    <Button
      btnType="tertiary"
      disabled={isBtnDisabled}
      grow
      noMargin
      onClick={onBtnClick}
      px="10px"
      pb={0}
    >
      <Box align="center" horizontal justify="center" flow={5}>
        {children}
      </Box>
    </Button>
  );
};

const Media = () => {
  const dispatch = useDispatch();
  const [triggerPostMediaRequest, getPostMediaRequestResult] =
    usePostMediaRequestMutation();

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const currentPSAP = useSelector(state => state.psaps.currentPSAP);

  const isMediaCardShown = useSelector(
    state => state.mapCard.cards[MEDIA_CARD],
  );

  const selectedCall = useSelectedCall();
  const hasAccessToDisplayMedia = useHasAccessToDisplayMedia();
  const hasAccessToLiveVideo = useHasAccessToLiveVideo();
  const hasAccessToRequestMedia = useHasAccessToRequestMedia();

  const mediaUploadData = useSelectedMediaData(selectedCallerID);
  const videoData = useSelectedVideoData(selectedCallerID);

  const mostRecentLoc =
    getMostRecentLocations(selectedCall)?.mostRecentLocation;

  const hasVideoStreamingCapability = useMemo(
    () => mostRecentLoc?.device_capabilities?.video_streaming,
    [mostRecentLoc],
  );

  const hasMediaUploadCapability = useMemo(
    () => mostRecentLoc?.device_capabilities?.media_upload,
    [mostRecentLoc],
  );

  const shouldShowDisplayMediaBtn = useMemo(() => {
    return (
      hasAccessToLiveVideo &&
      hasAccessToRequestMedia &&
      (mediaUploadData.length || !!videoData.whip_url)
    );
  }, [
    hasAccessToLiveVideo,
    hasAccessToRequestMedia,
    mediaUploadData,
    videoData,
  ]);

  const requestType = useMemo(
    () => getPostMediaRequestResult?.originalArgs?.requestType,
    [getPostMediaRequestResult?.originalArgs?.requestType],
  );

  const isLoading = useMemo(
    () => getPostMediaRequestResult?.isLoading,
    [getPostMediaRequestResult?.isLoading],
  );

  /**
   * Flag to render any of the components
   */
  const shouldRender = useMemo(() => {
    return (
      mostRecentLoc?.application?.toLowerCase() === 'ios' &&
      (hasAccessToLiveVideo ||
        hasAccessToRequestMedia ||
        hasAccessToDisplayMedia)
    );
  }, [
    hasAccessToDisplayMedia,
    hasAccessToLiveVideo,
    hasAccessToRequestMedia,
    mostRecentLoc?.application,
  ]);

  /**
   * Function that makes the API request
   * @param {String} videoStreamingOrMediaUpload - VIDEO_STREAMING or MEDIA_UPLOARD
   */
  const handleMediaRequest = useCallback(
    videoStreamingOrMediaUpload => {
      const data = {
        requestType: videoStreamingOrMediaUpload,
        accountId: currentPSAP.account_id,
        authorityName: currentPSAP.display_name,
        callerId: selectedCallerID,
        callStartTime: mostRecentLoc?.call_start_time.toString(),
      };
      triggerPostMediaRequest(data);
    },
    [
      currentPSAP.account_id,
      currentPSAP.display_name,
      mostRecentLoc?.call_start_time,
      selectedCallerID,
      triggerPostMediaRequest,
    ],
  );

  const triggerVideoStream = useCallback(() => {
    handleMediaRequest(VIDEO_STREAMING);
    trackMediaLiveVideoClick();
  }, [handleMediaRequest]);

  const triggerMediaUpload = useCallback(() => {
    handleMediaRequest(MEDIA_UPLOAD);
    trackMediaRequestMediaClick();
  }, [handleMediaRequest]);

  const showMediaUpload = useCallback(() => {
    dispatch(setMedia(MEDIA_UPLOAD));
    if (!isMediaCardShown) {
      dispatch(setCardsToRender(MEDIA_CARD));
    }
    trackDisplayMediaClick();
  }, [dispatch, isMediaCardShown]);

  useEffect(() => {
    if (
      getPostMediaRequestResult.status === 'fulfilled' &&
      getPostMediaRequestResult.originalArgs.requestType === VIDEO_STREAMING &&
      getPostMediaRequestResult.data?.whip_url
    ) {
      // Show card automatically when requesting live video
      dispatch(setCardsToRender(MEDIA_CARD));
    }
  }, [dispatch, getPostMediaRequestResult]);

  useEffect(() => {
    if (['fulfilled', 'rejected'].includes(getPostMediaRequestResult.status)) {
      trackMediaRequest({
        isVideoRequest:
          getPostMediaRequestResult.originalArgs.requestType ===
          VIDEO_STREAMING,
        responseSuccess: getPostMediaRequestResult.status === 'fulfilled',
        videoUrl: getPostMediaRequestResult.data?.whip_url,
        mediaUrl: getPostMediaRequestResult.data?.media_url,
      });
    }
  }, [getPostMediaRequestResult]);

  useEffect(() => {
    // Close the card whenever selectedCallerID changes
    dispatch(closeCard(MEDIA_CARD));
    return () => {
      dispatch(closeCard(MEDIA_CARD));
    };
  }, [dispatch, selectedCallerID]);

  useHandleMedia();

  if (!shouldRender) {
    return null;
  }

  return (
    <Box width="100%" mb="16px" gridRowGap="10px">
      {getPostMediaRequestResult?.isError && (
        <Box
          bg="lightDanger"
          horizontal
          justify="flex-start"
          px="20px"
          py="12px"
          width="100%"
        >
          <AttentionTriangleIcon color="background" width={16} height={16} />
          <Text color="background" ml="12px" size="normal">
            {requestType === VIDEO_STREAMING
              ? 'Live Video request failed'
              : 'Media request failed'}
          </Text>
        </Box>
      )}

      <Box gridColumnGap="10px" horizontal>
        {hasAccessToLiveVideo && hasVideoStreamingCapability && (
          <ButtonContainer
            isBtnDisabled={requestType === VIDEO_STREAMING && isLoading}
            onBtnClick={() => triggerVideoStream(selectedCallerID)}
          >
            <MeetingCameraIcon color="secondaryText" height={16} width={16} />
            <Text color="secondaryText">Live Video</Text>
          </ButtonContainer>
        )}
        {hasAccessToRequestMedia && hasMediaUploadCapability && (
          <ButtonContainer
            isBtnDisabled={requestType === MEDIA_UPLOAD && isLoading}
            onBtnClick={() => triggerMediaUpload(selectedCallerID)}
          >
            <PlayIcon color="secondaryText" height={16} width={16} />
            <Text color="secondaryText">Request Media</Text>
          </ButtonContainer>
        )}
      </Box>

      {shouldShowDisplayMediaBtn && (
        <ButtonContainer onBtnClick={showMediaUpload}>
          <Text color="secondaryText">Display Media</Text>
        </ButtonContainer>
      )}
    </Box>
  );
};

export default Media;
